import { type PropsWithClassName, cn } from "@/components/podkit/lib/cn";
import React from "react";

export type HideableCellProps = {
    hideOnSmallScreen?: boolean;
};

export const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement> & PropsWithClassName>(
    ({ className, ...props }, ref) => {
        return (
            <div className="relative w-full overflow-auto">
                <table ref={ref} className={cn("w-full text-left text-sm", className)} {...props} />
            </div>
        );
    },
);
Table.displayName = "Table";

export const TableHeader = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement> & PropsWithClassName
>(({ className, ...props }, ref) => {
    return (
        <thead
            ref={ref}
            className={cn(
                // extra padding on top to account for bottom border
                "[&_th:not([data-no-side-padding])]:px-4 [&_th]:pb-2 [&_th]:pt-3",
                "[&_th]:font-bold",
                "[&_th]:bg-surface-tertiary",
                "[&_th:first-child]:rounded-tl-md [&_th:last-child]:rounded-tr-md",
                className,
            )}
            {...props}
        />
    );
});
TableHeader.displayName = "TableHeader";

export const TableRow = React.forwardRef<
    HTMLTableRowElement,
    React.HTMLAttributes<HTMLTableRowElement> & PropsWithClassName
>(({ className, ...props }, ref) => {
    return <tr ref={ref} className={cn("border-b border-border-base last:border-none", className)} {...props} />;
});
TableRow.displayName = "TableRow";

export const TableHead = React.forwardRef<
    HTMLTableCellElement,
    React.ThHTMLAttributes<HTMLTableCellElement> & PropsWithClassName<HideableCellProps>
>(({ hideOnSmallScreen, className, ...props }, ref) => {
    return <th ref={ref} className={cn(hideOnSmallScreen && "hidden md:table-cell", className)} {...props} />;
});
TableHead.displayName = "TableHead";

export const TableBody = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement> & PropsWithClassName
>(({ className, ...props }, ref) => {
    return (
        <tbody
            ref={ref}
            className={cn("[&_tr]:hover:bg-muted/5 [&_td:last-child]:text-right [&_td]:px-4 [&_td]:py-2", className)}
            {...props}
        />
    );
});
TableBody.displayName = "TableBody";

export const TableCell = React.forwardRef<
    HTMLTableCellElement,
    React.TdHTMLAttributes<HTMLTableCellElement> & PropsWithClassName<HideableCellProps>
>(({ hideOnSmallScreen, className, ...props }, ref) => {
    return <td ref={ref} className={cn(hideOnSmallScreen && "hidden md:table-cell", className)} {...props} />;
});
TableCell.displayName = "TableCell";
